import { useCallback, useEffect, useRef, useState } from "react";
import { AuctionPlaceBid } from "../../redux/auth-reducer/auth-service";

/**
 * #183: how long after a pushed pool change the Auction Winnings block is fetched again - one
 * request for a burst of bids, not one per push.
 */
export const WINNINGS_REFETCH_DELAY_MS = 300;

/**
 * The Auction Winnings block of Auction Details (#237; the old PlaceBid page's before it): each
 * place's title, percentage and amount, from Auction/WinnerPercentage. The amounts are the
 * server's, worked out from the pool - never
 * here, since the payout base (gross or net of the Manager Hold) is the server's to decide.
 *
 * Fetched on mount, whatever the lot's own request does, so a lot that fails to load still shows
 * them; then after each later reload of the lot, and a moment after a push moved the pool.
 * FE-16 QA (#4): the lot's first load is not a reason to fetch again - the mount request already
 * answers it - and the page used to send the request twice whenever it opened.
 *
 * Only the newest request's reply is written, and only a list: no reply or a refusal keeps the
 * amounts on screen.
 *
 * @param {object} options
 * @param {number|string} options.auctionId The lot's auction.
 * @param {object|null} options.loadedRow The page's last read: a new object at every REST reload.
 * @param {number|null|undefined} options.totalPool The pool on screen.
 * @param {boolean} options.ended Bidding has ended; nothing is fetched for its pool any more.
 * @returns {Array|undefined} The places, as the server sent them; undefined until the first reply.
 */
export const useLotWinnings = ({ auctionId, loadedRow, totalPool, ended }) => {
  const [winnings, setWinnings] = useState();
  // The number of the latest request; a reply to an earlier one is out of date.
  const requestRef = useRef(0);

  const fetchWinnings = useCallback(async () => {
    const request = ++requestRef.current;
    const result = await AuctionPlaceBid(auctionId);
    if (request === requestRef.current && Array.isArray(result?.data)) setWinnings(result.data);
  }, [auctionId]);

  // Mount, and the page's auction - the page never changes it while open.
  useEffect(() => {
    const requests = requestRef;
    fetchWinnings();
    // Leaving the page: no reply may be written any more.
    return () => {
      requests.current += 1;
    };
  }, [fetchWinnings]);

  // Each reload of the lot after the first: a bid, a refusal, a recheck, the tab shown again, a
  // reconnect. The first is the load the page opens with, which the mount request already covers.
  const firstRowSeenRef = useRef(false);
  useEffect(() => {
    if (!loadedRow) return;
    if (!firstRowSeenRef.current) {
      firstRowSeenRef.current = true;
      return;
    }
    fetchWinnings();
  }, [loadedRow, fetchWinnings]);

  // #183: another player's bid moved the Total Pool heading but left every place amount on the old
  // pool, since they were fetched only on a reload of the lot. A pushed pool change fetches them
  // again, quietly and once per burst of pushes.
  const seenRef = useRef({ loadedRow, totalPool });
  useEffect(() => {
    const seen = seenRef.current;
    seenRef.current = { loadedRow, totalPool };
    // A reload of the lot fetches them itself (above); only a pool a push moved is left here.
    if (ended || loadedRow !== seen.loadedRow) return undefined;
    if (totalPool == null || Number(totalPool) === Number(seen.totalPool)) return undefined;

    const timer = setTimeout(fetchWinnings, WINNINGS_REFETCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [loadedRow, totalPool, ended, fetchWinnings]);

  return winnings;
};
